/**
 * Theming central de MUI (exigido por Framework.md).
 *
 * Dos disposiciones sobre la misma paleta, cada una en modo claro u oscuro:
 *
 * - `crearTemaAdmin`: back-office de administracion, en pantalla de escritorio.
 * - `crearTemaSalidas`: pantalla tactil de salidas. Tipografia y controles mas
 *   grandes porque se usa de pie, con guantes y con prisa, y porque el objetivo
 *   operativo de ese paso son diez segundos.
 *
 * El modo (claro/oscuro) lo elige el usuario y lo conserva `ProveedorTema`. La
 * paleta oscura es la misma identidad de la marca con los acentos aclarados para
 * que contrasten sobre fondo oscuro; es la que comparte el manual.
 */

import { createTheme, type PaletteOptions, type Theme } from '@mui/material/styles'
import { esES } from '@mui/material/locale'
import { esES as gridEsES } from '@mui/x-data-grid/locales'

export type ModoTema = 'light' | 'dark'

/** Paleta de la marca en cada modo. En oscuro se aclaran los acentos. */
function paletaPara(modo: ModoTema): PaletteOptions {
  if (modo === 'dark') {
    return {
      mode: 'dark',
      primary: { main: '#6fa8d6', light: '#93c1e4', dark: '#04263e' },
      secondary: { main: '#d9a24c' },
      success: { main: '#5cae7c' },
      warning: { main: '#d9a24c' },
      error: { main: '#e08a8a' },
      background: { default: '#0e1a22', paper: '#16242d' },
      text: { primary: '#e7eef2', secondary: '#aebcc4' },
      divider: 'rgba(255,255,255,0.12)',
    }
  }
  return {
    mode: 'light',
    primary: { main: '#0b3d62', light: '#3a6a8f', dark: '#04263e' },
    secondary: { main: '#8a5a00' },
    success: { main: '#1b6b3a' },
    warning: { main: '#8a5a00' },
    error: { main: '#a12222' },
    background: { default: '#f4f6f8', paper: '#ffffff' },
  }
}

const tipografiaBase = {
  fontFamily: [
    '"Segoe UI"',
    'Roboto',
    '-apple-system',
    'BlinkMacSystemFont',
    'Arial',
    'sans-serif',
  ].join(','),
}

export function crearTemaAdmin(modo: ModoTema): Theme {
  return createTheme(
    {
      palette: paletaPara(modo),
      typography: { ...tipografiaBase, fontSize: 14 },
      shape: { borderRadius: 6 },
      components: {
        MuiButton: { defaultProps: { disableElevation: true } },
        MuiTextField: { defaultProps: { size: 'small' } },
        MuiPaper: { defaultProps: { variant: 'outlined' } },
      },
    },
    esES,
    gridEsES,
  )
}

export function crearTemaSalidas(modo: ModoTema): Theme {
  return createTheme(
    {
      palette: paletaPara(modo),
      typography: {
        ...tipografiaBase,
        fontSize: 18,
        button: { fontSize: '1.15rem', fontWeight: 700 },
        h4: { fontWeight: 700 },
      },
      shape: { borderRadius: 8 },
      components: {
        MuiButton: {
          defaultProps: { disableElevation: true, size: 'large' },
          // Objetivos tactiles grandes: se pulsa con el dedo, no con el raton.
          styleOverrides: { root: { minHeight: 60, paddingInline: 28 } },
        },
        MuiTextField: { defaultProps: { size: 'medium' } },
        MuiInputBase: {
          styleOverrides: {
            input: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '0.04em' },
          },
        },
        MuiPaper: { defaultProps: { variant: 'outlined' } },
      },
    },
    esES,
    gridEsES,
  )
}
